// IncidentResponse.jsx
import { useEffect, useRef } from "react";
import { motion, useInView, useAnimation } from "framer-motion";

/* ── Icons ── */
const ActivityIcon = ({ className = "w-4 h-4 text-[#093A5B]" }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" />
  </svg>
);

const ShieldIcon = ({ className = "w-5 h-5 text-[#093A5B]" }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
  </svg>
);

const BellIcon = ({ className = "w-5 h-5 text-[#093A5B]" }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10.268 21a2 2 0 0 0 3.464 0" />
    <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
  </svg>
);

const MailIcon = ({ className = "w-5 h-5" }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-10 7L2 7" />
  </svg>
);

const features = [
  {
    title: "24/7 Monitoring",
    description:
      "We watch for threats and unusual activity throughout every day.",
    icon: ActivityIcon,
  },
  {
    title: "Quick Response",
    description:
      "If something looks wrong, we act immediately to protect your data.",
    icon: ShieldIcon,
  },
  {
    title: "We'll Keep You Informed",
    description:
      "If something happens, we'll tell you what, why, and how we're fixing it.",
    icon: BellIcon,
  },
];

const IncidentResponse = () => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [controls, isInView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
        duration: 0.6,
      },
    },
  };

  const featureVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
      },
    },
  };

  return (
    <section className="IncidentResponse px-6 py-20 lg:py-28 bg-gradient-to-b from-white to-[#093A5B]/[0.04] relative overflow-hidden">
      {/* Animated Circular Backgrounds */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute top-1/2 left-1/4 w-64 h-64 border-2 border-[#093A5B]/20 rounded-full"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute top-1/3 right-1/4 w-80 h-80 border-2 border-[#093A5B]/20 rounded-full"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.5, 0.2],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />
        <motion.div
          className="absolute bottom-1/4 left-1/3 w-48 h-48 border-2 border-[#093A5B]/10 rounded-full"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
        />
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-[#093A5B]/30 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: 0,
            }}
            animate={{
              y: [0, -50, 0],
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "easeInOut",
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>

      <div className="mx-auto max-w-7xl relative z-10" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={controls}
            variants={{
              visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
            }}
          >
            {/* Badge */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, type: "spring", delay: 0.1 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-[#093A5B]/10 mb-6 shadow-lg"
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <ActivityIcon />
              </motion.div>
              {/* text-[12px] md:text-base font-semibold text-[#093A5B] */}
              <span className="Security_Operations_para">
                Security Operations
              </span>
            </motion.div>

            {/* Heading */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className=" IncidentResponse_heading"
            >
              Always watching, always ready
            </motion.h2>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              // text-[15px] md:text-base text-gray-600 leading-relaxed mb-8 Security_Operations_paragraph
              className="Security_Operations_paragraph"
            >
              Our security team monitors systems around the clock to catch and
              fix problems before they affect your work.
            </motion.p>

            {/* Features List */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={controls}
              className="space-y-5"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={featureVariants}
                  whileHover={{ x: 8 }}
                  className="flex items-start gap-3 group"
                >
                  <motion.div
                    className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#093A5B]/10 to-[#093A5B]/5 flex items-center justify-center flex-shrink-0 group-hover:from-[#093A5B]/20 group-hover:to-[#093A5B]/10 transition-all duration-300"
                    whileHover={{
                      scale: 1.1,
                      rotate: [0, -10, 10, 0],
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <feature.icon />
                  </motion.div>
                  <div>
                    <h4 className="font-semibold text-black mb-1 group-hover:text-[#093A5B] transition-colors">
                      {feature.title}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Card - Report Security Concern */}
          <motion.div
            initial={{ opacity: 0, x: 50, scale: 0.95 }}
            animate={controls}
            variants={{
              visible: {
                opacity: 1,
                x: 0,
                scale: 1,
                transition: {
                  type: "spring",
                  damping: 15,
                  stiffness: 80,
                  duration: 0.6,
                  delay: 0.2,
                },
              },
            }}
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{
              scale: 1,
              y: -1,
            }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-gradient-to-br from-[#093A5B] to-[#093A5B]/80 rounded-3xl p-10 lg:p-12 shadow-2xl overflow-hidden"
          >
            {/* Animated Background Pattern */}
            <motion.div
              className="absolute inset-0 opacity-5"
              animate={{
                backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{
                backgroundImage: `
                  linear-gradient(45deg, white 25%, transparent 25%, transparent 75%, white 75%, white),
                  linear-gradient(45deg, white 25%, transparent 25%, transparent 75%, white 75%, white)
                `,
                backgroundSize: "60px 60px",
              }}
            />

            {/* Pulsing Glow Effect */}
            <motion.div
              className="absolute inset-0 bg-white/5 rounded-3xl"
              animate={{
                scale: [1, 1.05, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            <div className="relative text-center">
              {/* Icon Container */}
              <motion.div
                className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-6 shadow-xl"
                whileHover={{
                  rotate: [0, -10, 10, 0],
                  scale: 1.1,
                  y: -5,
                }}
                transition={{ duration: 0.4 }}
              >
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                  }}
                >
                  <ShieldIcon className="w-8 h-8 text-white" />
                </motion.div>
              </motion.div>

              {/* Title */}
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="text-2xl font-bold text-white mb-4"
              >
                Report a security concern
              </motion.h3>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="text-white/90 mb-6 leading-relaxed"
              >
                If you discover a security vulnerability or have concerns about
                platform security, contact our security team immediately.
              </motion.p>

              {/* Email Button */}
              <motion.a
                href="mailto:security@simplifyte.com"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.4, type: "spring" }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#093A5B] rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg cursor-pointer"
              >
                <MailIcon />
                security@simplifyte.com
              </motion.a>

              {/* Footer Note */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.5 }}
                className="text-sm text-white/70 mt-4"
              >
                Responsible disclosure encouraged
              </motion.p>
            </div>

            {/* Animated Bottom Border */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-white via-white/50 to-transparent"
              initial={{ scaleX: 0 }}
              whileHover={{ scaleX: 1 }}
              transition={{ duration: 0.4 }}
              style={{ transformOrigin: "left" }}
            />
          </motion.div>
        </div>
      </div>

      {/* Custom CSS Animations */}
      <style jsx>{`
        .IncidentResponse {
          --incident-primary: #093a5b;
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) translateX(0px);
          }
          50% {
            transform: translateY(-20px) translateX(10px);
          }
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          100% {
            transform: scale(1.2);
            opacity: 0;
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes rotate-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-pulse-ring {
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        .animate-shimmer {
          animation: shimmer 2s infinite;
        }

        .animate-rotate-slow {
          animation: rotate-slow 20s linear infinite;
        }

        /* Hover Effects */
        .group:hover .group-hover\:scale-105 {
          transform: scale(1.05);
        }

        /* Responsive Adjustments */
        @media (max-width: 640px) {
          .IncidentResponse {
            padding-left: 1rem;
            padding-right: 1rem;
          }
        }

        @media (min-width: 768px) and (max-width: 1024px) {
          .IncidentResponse {
            padding-left: 2rem;
            padding-right: 2rem;
          }
        }

        /* Smooth Scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Custom Scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb {
          background: var(--incident-primary);
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #0a4a6e;
        }

        /* Selection Style */
        ::selection {
          background: var(--incident-primary);
          color: white;
        }

        /* Focus Styles */
        a:focus-visible {
          outline: 2px solid var(--incident-primary);
          outline-offset: 2px;
          border-radius: 12px;
        }
      `}</style>
    </section>
  );
};

export default IncidentResponse;
